import type { LinearQuestRoute, QuestRoute, StagedQuestRoute } from "./types";

export const ROUTE_CATALOG_VERSION = "2026-05-quest-v1";

export const moonTrekRoute: LinearQuestRoute = {
  routeId: "moon-trek",
  routeVersionId: "moon-trek-tranquillitatis-to-tycho-v1",
  schemaVersion: 1,
  title: "Moon Trek",
  description:
    "A grand lunar nearside expedition from Mare Tranquillitatis toward Tycho Crater.",
  kind: "linear_route",
  status: "enabled",
  progressMetric: "steps",
  gameDistanceMeters: 161_000,
  realDistanceNote:
    "Scaled quest distance. The named lunar places represent a symbolic nearside expedition, not a surveyed walking route.",
  displayDistanceMode: "scaled_distance",
  renderMode: "scene",
  progressPolicy: {
    movementSource: "steps",
    strideMeters: 0.762,
    gameDistanceMeters: 161_000,
    displayDistanceMode: "scaled_distance",
  },
  assets: [
    {
      assetId: "moon-trek-hero",
      kind: "hero",
      path: "/assets/quests/moon-trek/hero.png",
    },
    {
      assetId: "moon-trek-sight-mare-tranquillitatis",
      kind: "sight",
      path: "/assets/quests/moon-trek/sight-mare-tranquillitatis.png",
    },
    {
      assetId: "moon-trek-map-lroc-color-2k",
      kind: "map",
      path: "/assets/quests/moon-trek/map-lroc-color-2k.jpg",
    },
    {
      assetId: "moon-trek-badge-complete",
      kind: "badge",
      path: "/assets/quests/moon-trek/badge-complete.png",
    },
    {
      assetId: "moon-trek-item-regolith-compass",
      kind: "item",
      path: "/assets/quests/moon-trek/item-regolith-compass.png",
    },
    {
      assetId: "moon-trek-item-moonstone-lens",
      kind: "item",
      path: "/assets/quests/moon-trek/item-moonstone-lens.png",
    },
    {
      assetId: "moon-trek-item-tycho-starblade",
      kind: "item",
      path: "/assets/quests/moon-trek/item-tycho-starblade.png",
    },
  ],
  landmarks: [
    {
      landmarkId: "mare-tranquillitatis",
      title: "Mare Tranquillitatis Camp",
      atPercent: 10,
      description: "Apollo-era lunar plain with Earth high in the black sky.",
      url: "https://planetarynames.wr.usgs.gov/Feature/3691",
      coordinates: { lat: 8.5, lon: 31.4 },
      assetId: "moon-trek-sight-mare-tranquillitatis",
    },
    {
      landmarkId: "statio-tranquillitatis",
      title: "Statio Tranquillitatis",
      atPercent: 20,
      description: "Quiet bootprints near the Apollo 11 landing region.",
      url: "https://planetarynames.wr.usgs.gov/Feature/5684",
      coordinates: { lat: 0.7, lon: 23.5 },
    },
    {
      landmarkId: "mare-serenitatis",
      title: "Mare Serenitatis Ridge",
      atPercent: 30,
      description: "A silver-gray mare ridge under sharp lunar light.",
      url: wikipediaUrl("Mare Serenitatis"),
      coordinates: { lat: 28.0, lon: 17.5 },
    },
    {
      landmarkId: "hadley-rille",
      title: "Hadley Rille Crossing",
      atPercent: 40,
      description: "A winding rille crossing through pale regolith.",
      url: "https://planetarynames.wr.usgs.gov/Feature/5064",
      coordinates: { lat: 26.2, lon: 3.7 },
    },
    {
      landmarkId: "copernicus-rays",
      title: "Copernicus Crater Rays",
      atPercent: 55,
      description: "Bright crater ray terrain near a distant rim.",
      url: "https://planetarynames.wr.usgs.gov/Feature/1296",
      coordinates: { lat: 9.7, lon: -20.1 },
    },
    {
      landmarkId: "clavius-basin",
      title: "Clavius Basin Approach",
      atPercent: 72,
      description: "Layered rims around a vast ancient crater basin.",
      url: "https://planetarynames.wr.usgs.gov/Feature/1236",
      coordinates: { lat: -58.4, lon: -14.4 },
    },
    {
      landmarkId: "tycho-rim",
      title: "Tycho Rim Approach",
      atPercent: 88,
      description: "A steep crater wall and a star-filled horizon.",
      url: "https://planetarynames.wr.usgs.gov/Feature/6163",
      coordinates: { lat: -43.3, lon: -11.2 },
    },
    {
      landmarkId: "tycho-overlook",
      title: "Tycho Central Peak Overlook",
      atPercent: 100,
      description: "The journey ends at a bright crater overlook.",
      url: "https://planetarynames.wr.usgs.gov/Feature/6163",
      coordinates: { lat: -43.3, lon: -11.2 },
    },
  ],
  rewards: [
    {
      rewardId: "moon-trek-sight-mare-tranquillitatis",
      kind: "sight",
      title: "Mare Tranquillitatis Sight",
      unlock: { atPercent: 10 },
      assetId: "moon-trek-sight-mare-tranquillitatis",
    },
    {
      rewardId: "moon-trek-regolith-compass",
      kind: "chest",
      title: "Regolith Compass",
      unlock: { atPercent: 33 },
      assetId: "moon-trek-item-regolith-compass",
    },
    {
      rewardId: "moon-trek-moonstone-lens",
      kind: "chest",
      title: "Moonstone Lens",
      unlock: { atPercent: 66 },
      assetId: "moon-trek-item-moonstone-lens",
    },
    {
      rewardId: "moon-trek-tycho-starblade",
      kind: "chest",
      title: "Tycho Starblade",
      unlock: { completion: true },
      assetId: "moon-trek-item-tycho-starblade",
    },
    {
      rewardId: "moon-trek-complete",
      kind: "badge",
      title: "Moon Trek Complete",
      unlock: { completion: true },
      assetId: "moon-trek-badge-complete",
    },
  ],
  shareMeta: {
    title: "Moon Trek",
    description: "Turn daily movement into a grand lunar quest.",
    ogImageAssetId: "moon-trek-hero",
  },
  sourceNotes: [
    "Lunar place names are factual. Initial art may use generated assets and public-domain NASA/USGS references.",
    "Moon Trek map backdrop uses NASA Scientific Visualization Studio CGI Moon Kit LROC color map, assembled from Lunar Reconnaissance Orbiter data: https://svs.gsfc.nasa.gov/4720",
    "Lunar marker coordinates are approximate named-feature positions for map placement, not a surveyed walking route.",
  ],
};

export const marsTraverseRoute: LinearQuestRoute = {
  routeId: "mars-traverse",
  routeVersionId: "mars-traverse-jezero-to-olympus-v1",
  schemaVersion: 1,
  title: "Mars Traverse",
  description:
    "A symbolic red-planet journey from Jezero Crater toward the summit caldera of Olympus Mons.",
  kind: "linear_route",
  status: "planned",
  progressMetric: "steps",
  gameDistanceMeters: 243_000,
  realDistanceNote:
    "Scaled quest distance. The named Mars locations span thousands of real kilometers and are ordered as an iconic expedition, not a surveyed traversable route.",
  displayDistanceMode: "scaled_distance",
  renderMode: "static_map",
  progressPolicy: {
    movementSource: "steps",
    strideMeters: 0.762,
    gameDistanceMeters: 243_000,
    displayDistanceMode: "scaled_distance",
  },
  assets: [
    {
      assetId: "mars-traverse-hero",
      kind: "hero",
      path: "/assets/quests/mars-traverse/hero.png",
    },
    {
      assetId: "mars-traverse-sight-jezero-delta",
      kind: "sight",
      path: "/assets/quests/mars-traverse/sight-jezero-delta.png",
    },
    {
      assetId: "mars-traverse-map-viking-mdim21",
      kind: "map",
      path: "/assets/quests/mars-traverse/map-viking-mdim21-color-1km.webp",
    },
    {
      assetId: "mars-traverse-badge-complete",
      kind: "badge",
      path: "/assets/quests/mars-traverse/badge-complete.png",
    },
    {
      assetId: "mars-traverse-item-basalt-compass",
      kind: "item",
      path: "/assets/quests/mars-traverse/item-basalt-compass.png",
    },
    {
      assetId: "mars-traverse-item-canyon-crystal",
      kind: "item",
      path: "/assets/quests/mars-traverse/item-canyon-crystal.png",
    },
    {
      assetId: "mars-traverse-item-volcano-sigil",
      kind: "item",
      path: "/assets/quests/mars-traverse/item-volcano-sigil.png",
    },
  ],
  landmarks: [
    {
      landmarkId: "jezero-delta",
      title: "Jezero Delta Outlook",
      atPercent: 8,
      description:
        "A dry present-day overlook across the ancient delta fan inside Jezero Crater, where Perseverance studies layered sediments left by a long-vanished lake.",
      url: "https://planetarynames.wr.usgs.gov/Feature/14300",
      coordinates: { lat: 18.38, lon: 77.58 },
      assetId: "mars-traverse-sight-jezero-delta",
    },
    {
      landmarkId: "arabia-terra",
      title: "Arabia Terra Crossing",
      atPercent: 19,
      description:
        "A traverse across ancient cratered highlands west of Jezero, using Arabia Terra as the first broad step away from the landing-region neighborhood.",
      url: wikipediaUrl("Arabia Terra"),
      coordinates: { lat: 21, lon: 5 },
    },
    {
      landmarkId: "meridiani-planum",
      title: "Meridiani Planum Flats",
      atPercent: 31,
      description:
        "A low, open equatorial plain famous from the Opportunity rover era, placed here as a clear westward waypoint before the canyon country.",
      url: wikipediaUrl("Meridiani Planum"),
      coordinates: { lat: 0.2, lon: -2.5 },
    },
    {
      landmarkId: "ganges-chasma",
      title: "Ganges Chasma Gate",
      atPercent: 43,
      description:
        "The eastern approach to the Valles Marineris canyon system, with the route dropping from open plains toward immense troughs.",
      url: wikipediaUrl("Ganges Chasma"),
      coordinates: { lat: -8, lon: -44 },
    },
    {
      landmarkId: "valles-marineris-east",
      title: "Valles Marineris Eastern Gate",
      atPercent: 58,
      description:
        "A rimward approach to the eastern reaches of Valles Marineris, where enormous canyon troughs cut across the equatorial highlands.",
      url: "https://planetarynames.wr.usgs.gov/Feature/6288",
      coordinates: { lat: -13.8, lon: -58 },
    },
    {
      landmarkId: "candor-chasma",
      title: "Candor Chasma Traverse",
      atPercent: 66,
      description:
        "A central Valles Marineris passage through layered canyon terrain, chosen as a visually distinct midpoint on the long westward crossing.",
      url: wikipediaUrl("Candor Chasma"),
      coordinates: { lat: -6.5, lon: -72 },
    },
    {
      landmarkId: "noctis-labyrinthus",
      title: "Noctis Labyrinthus Passage",
      atPercent: 78,
      description:
        "A maze-like network of intersecting valleys and collapsed blocks at the western end of Valles Marineris and the edge of Tharsis.",
      url: "https://planetarynames.wr.usgs.gov/Feature/4324",
      coordinates: { lat: -7, lon: -102 },
    },
    {
      landmarkId: "pavonis-mons",
      title: "Pavonis Mons Shoulder",
      atPercent: 88,
      description:
        "A waypoint on the middle Tharsis shield volcano, making the final volcanic approach to Olympus Mons easier to read on the map.",
      url: wikipediaUrl("Pavonis Mons"),
      coordinates: { lat: 0.8, lon: -112.8 },
    },
    {
      landmarkId: "olympus-mons-caldera",
      title: "Olympus Mons Caldera Rim",
      atPercent: 100,
      description:
        "The finish line on the summit-caldera region of Olympus Mons, the largest known volcano in the Solar System and a landmark that dominates western Tharsis.",
      url: "https://planetarynames.wr.usgs.gov/Feature/4453",
      coordinates: { lat: 18.65, lon: -133.8 },
    },
  ],
  rewards: [
    {
      rewardId: "mars-traverse-sight-jezero",
      kind: "sight",
      title: "Jezero Delta Sight",
      unlock: { atPercent: 8 },
      assetId: "mars-traverse-sight-jezero-delta",
    },
    {
      rewardId: "mars-traverse-basalt-compass",
      kind: "chest",
      title: "Basalt Compass",
      unlock: { atPercent: 33 },
      assetId: "mars-traverse-item-basalt-compass",
    },
    {
      rewardId: "mars-traverse-canyon-crystal",
      kind: "chest",
      title: "Canyon Crystal",
      unlock: { atPercent: 66 },
      assetId: "mars-traverse-item-canyon-crystal",
    },
    {
      rewardId: "mars-traverse-volcano-sigil",
      kind: "chest",
      title: "Volcano Sigil",
      unlock: { completion: true },
      assetId: "mars-traverse-item-volcano-sigil",
    },
    {
      rewardId: "mars-traverse-complete",
      kind: "badge",
      title: "Mars Traverse Complete",
      unlock: { completion: true },
      assetId: "mars-traverse-badge-complete",
    },
  ],
  shareMeta: {
    title: "Mars Traverse",
    description: "Turn daily movement into a red-planet expedition.",
    ogImageAssetId: "mars-traverse-hero",
  },
  sourceNotes: [
    "Jezero Crater is the Perseverance rover landing region on the western edge of Isidis Planitia, per NASA/JPL public mission materials.",
    "Valles Marineris, the Tharsis volcanoes, and Olympus Mons are factual Mars landmarks documented by NASA/USGS public resources.",
    "Mars Traverse map backdrop uses the USGS/Wikimedia Mars Viking MDIM21 global color mosaic in simple cylindrical projection: https://commons.wikimedia.org/wiki/File:Mars_Viking_MDIM21_ClrMosaic_1km.jpg",
    "Marker coordinates are approximate named-feature positions for map placement, not a surveyed traversable route.",
    "Initial art should use generated assets and public-domain NASA/JPL/USGS references; present-day Mars imagery must stay dry unless explicitly depicting ancient water history in text.",
  ],
};

export const appalachianTrailRoute: LinearQuestRoute = {
  routeId: "appalachian-trail",
  routeVersionId: "appalachian-trail-2026-nobo-v1",
  schemaVersion: 1,
  title: "Appalachian Trail",
  description:
    "A northbound virtual thru-hike from Springer Mountain, Georgia, to Katahdin, Maine.",
  kind: "linear_route",
  status: "planned",
  progressMetric: "distance",
  gameDistanceMeters: milesToMeters(2_197.9),
  realDistanceNote:
    "Uses the Appalachian Trail Conservancy 2026 official A.T. length of 2,197.9 miles. Milestone distances are approximate route positions for gameplay.",
  displayDistanceMode: "real_distance",
  renderMode: "static_map",
  progressPolicy: {
    movementSource: "distance",
    strideMeters: 0.762,
    gameDistanceMeters: milesToMeters(2_197.9),
    displayDistanceMode: "real_distance",
  },
  assets: [
    {
      assetId: "appalachian-trail-hero",
      kind: "hero",
      path: "/assets/quests/appalachian-trail/hero.png",
    },
    {
      assetId: "appalachian-trail-sight-springer-mountain",
      kind: "sight",
      path: "/assets/quests/appalachian-trail/sight-springer-mountain.png",
    },
    {
      assetId: "appalachian-trail-badge-complete",
      kind: "badge",
      path: "/assets/quests/appalachian-trail/badge-complete.png",
    },
    {
      assetId: "appalachian-trail-item-white-blaze-compass",
      kind: "item",
      path: "/assets/quests/appalachian-trail/item-white-blaze-compass.png",
    },
    {
      assetId: "appalachian-trail-item-ridge-crystal",
      kind: "item",
      path: "/assets/quests/appalachian-trail/item-ridge-crystal.png",
    },
    {
      assetId: "appalachian-trail-item-summit-staff",
      kind: "item",
      path: "/assets/quests/appalachian-trail/item-summit-staff.png",
    },
  ],
  landmarks: [
    {
      landmarkId: "springer-mountain",
      title: "Springer Mountain",
      atMeters: milesToMeters(0),
      description: "The southern terminus and first white blaze.",
      url: "https://www.fs.usda.gov/recarea/conf/recarea/?recid=10539",
      assetId: "appalachian-trail-sight-springer-mountain",
    },
    {
      landmarkId: "blood-mountain",
      title: "Blood Mountain",
      atMeters: milesToMeters(31),
      description: "A high Georgia summit with broad Blue Ridge views.",
      url: "https://www.fs.usda.gov/recarea/conf/recarea/?recid=10542",
    },
    {
      landmarkId: "kuwohi",
      title: "Kuwohi",
      atMeters: milesToMeters(200),
      description:
        "The high point of Great Smoky Mountains National Park, formerly known as Clingmans Dome.",
      url: "https://www.nps.gov/articles/000/ancient-name-returns-to-smokies-highest-peak.htm",
    },
    {
      landmarkId: "damascus",
      title: "Damascus Trail Town",
      atMeters: milesToMeters(470),
      description: "A welcoming Virginia trail town and classic hiker crossroads.",
      url: "https://en.wikipedia.org/wiki/Damascus,_Virginia",
    },
    {
      landmarkId: "grayson-highlands",
      title: "Grayson Highlands",
      atMeters: milesToMeters(500),
      description: "Open balds and wind-shaped high-country scenery.",
      url: "https://www.dcr.virginia.gov/state-parks/grayson-highlands",
    },
    {
      landmarkId: "mcafee-knob",
      title: "McAfee Knob",
      atMeters: milesToMeters(714),
      description: "A signature Virginia overlook above the Catawba Valley.",
      url: "https://www.nps.gov/articles/mcafee-knob.htm",
    },
    {
      landmarkId: "shenandoah",
      title: "Shenandoah Skyline",
      atMeters: milesToMeters(864),
      description: "A long green ridge walk through Shenandoah National Park.",
      url: "https://www.nps.gov/shen/index.htm",
    },
    {
      landmarkId: "harpers-ferry",
      title: "Harpers Ferry",
      atMeters: milesToMeters(1_024),
      description: "The psychological midpoint and home of the A.T. Conservancy.",
      url: "https://appalachiantrail.org/explore/communities/harpers-ferry-bolivar/",
    },
    {
      landmarkId: "delaware-water-gap",
      title: "Delaware Water Gap",
      atMeters: milesToMeters(1_295),
      description: "A river passage between Pennsylvania and New Jersey ridges.",
      url: "https://www.nps.gov/dewa/index.htm",
    },
    {
      landmarkId: "mount-greylock",
      title: "Mount Greylock",
      atMeters: milesToMeters(1_585),
      description: "Massachusetts high country and a tower above rolling hills.",
      url: "https://www.mass.gov/locations/mount-greylock-state-reservation",
    },
    {
      landmarkId: "killington-peak",
      title: "Killington Peak",
      atMeters: milesToMeters(1_700),
      description: "A Vermont mountain landmark along the Long Trail corridor.",
      url: wikipediaUrl("Killington Peak"),
    },
    {
      landmarkId: "mount-washington",
      title: "Mount Washington",
      atMeters: milesToMeters(1_860),
      description: "A dramatic Presidential Range summit above alpine terrain.",
      url: "https://www.fs.usda.gov/recarea/whitemountain/recarea/?recid=74665",
    },
    {
      landmarkId: "hundred-mile-wilderness",
      title: "Hundred-Mile Wilderness",
      atMeters: milesToMeters(2_075),
      description: "A remote Maine passage before the final mountain.",
      url: wikipediaUrl("Hundred-Mile Wilderness"),
    },
    {
      landmarkId: "katahdin",
      title: "Katahdin",
      atMeters: milesToMeters(2_197.9),
      description: "The northern terminus and a clear summit-day celebration.",
      url: "https://baxterstatepark.org/mountain/",
    },
  ],
  rewards: [
    {
      rewardId: "appalachian-trail-sight-springer",
      kind: "sight",
      title: "Springer Mountain Sight",
      unlock: { atMeters: milesToMeters(0) },
      assetId: "appalachian-trail-sight-springer-mountain",
    },
    {
      rewardId: "appalachian-trail-blaze-compass",
      kind: "chest",
      title: "White Blaze Compass",
      unlock: { atPercent: 33 },
      assetId: "appalachian-trail-item-white-blaze-compass",
    },
    {
      rewardId: "appalachian-trail-ridge-crystal",
      kind: "chest",
      title: "Ridge Crystal",
      unlock: { atPercent: 66 },
      assetId: "appalachian-trail-item-ridge-crystal",
    },
    {
      rewardId: "appalachian-trail-summit-staff",
      kind: "chest",
      title: "Summit Staff",
      unlock: { completion: true },
      assetId: "appalachian-trail-item-summit-staff",
    },
    {
      rewardId: "appalachian-trail-complete",
      kind: "badge",
      title: "Appalachian Trail Complete",
      unlock: { completion: true },
      assetId: "appalachian-trail-badge-complete",
    },
  ],
  shareMeta: {
    title: "Appalachian Trail",
    description: "Turn daily movement into a northbound A.T. quest.",
    ogImageAssetId: "appalachian-trail-hero",
  },
  sourceNotes: [
    "2026 official route length: 2,197.9 miles, published by the Appalachian Trail Conservancy: https://appalachiantrail.org/news-stories/2026-official-mileage/",
    "A.T. Basics route summary: https://appalachiantrail.org/experience/hike-the-trail/at-basics/",
    "Kuwohi naming reference: https://www.nps.gov/articles/000/ancient-name-returns-to-smokies-highest-peak.htm",
    "The route is northbound from Springer Mountain to Katahdin and passes through 14 states.",
    "Milestone distances are approximate gameplay positions until an official data-book import is added.",
  ],
};

export const pacificCrestTrailRoute: LinearQuestRoute = {
  routeId: "pacific-crest-trail",
  routeVersionId: "pacific-crest-trail-public-2650-nobo-v1",
  schemaVersion: 1,
  title: "Pacific Crest Trail",
  description:
    "A northbound virtual thru-hike from the southern monument near Campo to the Canadian border.",
  kind: "linear_route",
  status: "planned",
  progressMetric: "distance",
  gameDistanceMeters: milesToMeters(2_650),
  realDistanceNote:
    "Uses the stable public-facing PCT length of 2,650 miles. PCTA centerline data currently estimates 2,655.84 miles and may be imported later for higher precision.",
  displayDistanceMode: "real_distance",
  renderMode: "static_map",
  progressPolicy: {
    movementSource: "distance",
    strideMeters: 0.762,
    gameDistanceMeters: milesToMeters(2_650),
    displayDistanceMode: "real_distance",
  },
  assets: [
    {
      assetId: "pacific-crest-trail-hero",
      kind: "hero",
      path: "/assets/quests/pacific-crest-trail/hero.png",
    },
    {
      assetId: "pacific-crest-trail-sight-southern-terminus",
      kind: "sight",
      path: "/assets/quests/pacific-crest-trail/sight-southern-terminus.png",
    },
    {
      assetId: "pacific-crest-trail-badge-complete",
      kind: "badge",
      path: "/assets/quests/pacific-crest-trail/badge-complete.png",
    },
    {
      assetId: "pacific-crest-trail-item-desert-compass",
      kind: "item",
      path: "/assets/quests/pacific-crest-trail/item-desert-compass.png",
    },
    {
      assetId: "pacific-crest-trail-item-cascade-crystal",
      kind: "item",
      path: "/assets/quests/pacific-crest-trail/item-cascade-crystal.png",
    },
    {
      assetId: "pacific-crest-trail-item-border-lantern",
      kind: "item",
      path: "/assets/quests/pacific-crest-trail/item-border-lantern.png",
    },
  ],
  landmarks: [
    {
      landmarkId: "southern-terminus",
      title: "Southern Terminus",
      atMeters: milesToMeters(0),
      description: "The border monument near Campo and the first desert miles.",
      url: "https://www.pcta.org/discover-the-trail/terminus/southern-terminus/",
      assetId: "pacific-crest-trail-sight-southern-terminus",
    },
    {
      landmarkId: "mount-laguna",
      title: "Mount Laguna",
      atMeters: milesToMeters(43),
      description: "A cool pine crest above the Southern California desert.",
      url: "https://www.fs.usda.gov/recarea/cleveland/recarea/?recid=47560",
    },
    {
      landmarkId: "warner-springs",
      title: "Warner Springs Meadow",
      atMeters: milesToMeters(109),
      description: "Open grassland and an early-trail community stop.",
      url: wikipediaUrl("Warner Springs, California"),
    },
    {
      landmarkId: "big-bear",
      title: "Big Bear Ridge",
      atMeters: milesToMeters(266),
      description: "A mountain-town breather after long desert climbs.",
      url: wikipediaUrl("Big Bear Lake, California"),
    },
    {
      landmarkId: "agua-dulce",
      title: "Agua Dulce",
      atMeters: milesToMeters(454),
      description: "Sandstone country and a classic southern PCT resupply stop.",
      url: wikipediaUrl("Agua Dulce, California"),
    },
    {
      landmarkId: "kennedy-meadows-south",
      title: "Kennedy Meadows South",
      atMeters: milesToMeters(702),
      description: "The symbolic gateway from desert into the High Sierra.",
      url: wikipediaUrl("Kennedy Meadows, California"),
    },
    {
      landmarkId: "forester-pass",
      title: "Forester Pass",
      atMeters: milesToMeters(779),
      description: "The PCT high point in the Sierra Nevada.",
      url: wikipediaUrl("Forester Pass"),
    },
    {
      landmarkId: "tuolumne-meadows",
      title: "Tuolumne Meadows",
      atMeters: milesToMeters(942),
      description: "Granite domes, clear water, and Yosemite high country.",
      url: "https://www.nps.gov/yose/planyourvisit/tm.htm",
    },
    {
      landmarkId: "tahoe-rim",
      title: "Tahoe Rim",
      atMeters: milesToMeters(1_090),
      description: "Blue lake glimpses and volcanic ridge walking.",
      url: "https://tahoerimtrail.org/",
    },
    {
      landmarkId: "burney-falls",
      title: "Burney Falls",
      atMeters: milesToMeters(1_420),
      description: "A waterfall pause in Northern California.",
      url: "https://www.parks.ca.gov/?page_id=455",
    },
    {
      landmarkId: "ashland",
      title: "Ashland Gateway",
      atMeters: milesToMeters(1_718),
      description:
        "The Oregon border crossing region and a friendly nearby trail town.",
      url: wikipediaUrl("Ashland, Oregon"),
    },
    {
      landmarkId: "crater-lake-rim",
      title: "Crater Lake / Rim Alternate",
      atMeters: milesToMeters(1_830),
      description:
        "A deep-blue volcanic lake and the popular rim alternate that rejoins the PCT.",
      url: "https://www.nps.gov/crla/index.htm",
    },
    {
      landmarkId: "timberline-lodge",
      title: "Timberline Lodge",
      atMeters: milesToMeters(2_097),
      description: "A Mount Hood landmark and a generous high-elevation rest.",
      url: "https://www.fs.usda.gov/recarea/mthood/recarea/?recid=52770",
    },
    {
      landmarkId: "bridge-of-the-gods",
      title: "Bridge of the Gods",
      atMeters: milesToMeters(2_150),
      description: "The Columbia River crossing into Washington.",
      url: wikipediaUrl("Bridge of the Gods (modern structure)"),
    },
    {
      landmarkId: "goat-rocks",
      title: "Goat Rocks Crest",
      atMeters: milesToMeters(2_286),
      description: "A dramatic volcanic ridgeline with big Cascade views.",
      url: "https://www.fs.usda.gov/recarea/giffordpinchot/recarea/?recid=79409",
    },
    {
      landmarkId: "stehekin",
      title: "Stehekin",
      atMeters: milesToMeters(2_570),
      description: "A remote North Cascades village before the final push.",
      url: "https://www.nps.gov/noca/planyourvisit/stehekin.htm",
    },
    {
      landmarkId: "northern-terminus",
      title: "Northern Terminus",
      atMeters: milesToMeters(2_650),
      description: "The Canadian border monument and a quiet finish celebration.",
      url: "https://www.pcta.org/discover-the-trail/terminus/northern-terminus/",
    },
  ],
  rewards: [
    {
      rewardId: "pacific-crest-trail-sight-southern-terminus",
      kind: "sight",
      title: "Southern Terminus Sight",
      unlock: { atMeters: milesToMeters(0) },
      assetId: "pacific-crest-trail-sight-southern-terminus",
    },
    {
      rewardId: "pacific-crest-trail-desert-compass",
      kind: "chest",
      title: "Desert Compass",
      unlock: { atPercent: 33 },
      assetId: "pacific-crest-trail-item-desert-compass",
    },
    {
      rewardId: "pacific-crest-trail-cascade-crystal",
      kind: "chest",
      title: "Cascade Crystal",
      unlock: { atPercent: 66 },
      assetId: "pacific-crest-trail-item-cascade-crystal",
    },
    {
      rewardId: "pacific-crest-trail-border-lantern",
      kind: "chest",
      title: "Border Lantern",
      unlock: { completion: true },
      assetId: "pacific-crest-trail-item-border-lantern",
    },
    {
      rewardId: "pacific-crest-trail-complete",
      kind: "badge",
      title: "Pacific Crest Trail Complete",
      unlock: { completion: true },
      assetId: "pacific-crest-trail-badge-complete",
    },
  ],
  shareMeta: {
    title: "Pacific Crest Trail",
    description: "Turn daily movement into a Mexico-to-Canada PCT quest.",
    ogImageAssetId: "pacific-crest-trail-hero",
  },
  sourceNotes: [
    "PCTA Discover the Trail describes the PCT as 2,650 miles from Mexico to Canada: https://www.pcta.org/discover-the-trail/",
    "U.S. Forest Service PCT page describes a 2,650-mile continuous path between Mexico and Canada: https://www.fs.usda.gov/pct",
    "PCTA data page currently lists 2,655.84 miles as the latest centerline estimate: https://www.pcta.org/discover-the-trail/maps/pct-data/",
    "Milestone distances are approximate gameplay positions until official centerline data is imported.",
  ],
};

export const fourMoonsRoute: StagedQuestRoute = {
  routeId: "four-moons-of-jupiter",
  routeVersionId: "four-moons-of-jupiter-grand-tour-v1",
  schemaVersion: 1,
  title: "Four Moons of Jupiter",
  description:
    "A staged grand tour across Io, Europa, Ganymede, and Callisto with transfer gates between worlds.",
  kind: "staged_route",
  status: "planned",
  progressMetric: "steps",
  gameDistanceMeters: 320_000,
  realDistanceNote:
    "Scaled quest distance. The Galilean moons are separate worlds orbiting Jupiter; this is a symbolic grand-tour itinerary, not a continuous surface walk.",
  displayDistanceMode: "scaled_distance",
  renderMode: "scene",
  progressPolicy: {
    movementSource: "steps",
    strideMeters: 0.762,
    gameDistanceMeters: 320_000,
    displayDistanceMode: "scaled_distance",
  },
  assets: [
    {
      assetId: "four-moons-hero",
      kind: "hero",
      path: "/assets/quests/four-moons/hero.png",
    },
    {
      assetId: "four-moons-sight-io-jupiter-rise",
      kind: "sight",
      path: "/assets/quests/four-moons/sight-io-jupiter-rise.png",
    },
    {
      assetId: "four-moons-badge-complete",
      kind: "badge",
      path: "/assets/quests/four-moons/badge-complete.png",
    },
    {
      assetId: "four-moons-item-volcanic-compass",
      kind: "item",
      path: "/assets/quests/four-moons/item-volcanic-compass.png",
    },
    {
      assetId: "four-moons-item-icebound-crystal",
      kind: "item",
      path: "/assets/quests/four-moons/item-icebound-crystal.png",
    },
    {
      assetId: "four-moons-item-jovian-sigil",
      kind: "item",
      path: "/assets/quests/four-moons/item-jovian-sigil.png",
    },
  ],
  stages: [
    {
      stageId: "io",
      title: "Io",
      body: "A volcanic opening chapter across Jupiter's innermost Galilean moon.",
      landmarks: [
        {
          landmarkId: "io-jupiter-rise",
          title: "Io Jupiter-Rise Camp",
          atPercent: 5,
          description: "A bright volcanic plain with Jupiter filling the sky.",
          url: "https://science.nasa.gov/jupiter/moons/io/",
          assetId: "four-moons-sight-io-jupiter-rise",
        },
        {
          landmarkId: "io-loki-patera",
          title: "Loki Patera Overlook",
          atPercent: 14,
          description: "A respectful pass near one of Io's famous volcanic regions.",
          url: "https://science.nasa.gov/photojournal/loki-patera/",
        },
        {
          landmarkId: "io-pele-plume",
          title: "Pele Plume Watch",
          atPercent: 24,
          description: "A distant plume-viewing stop on colorful sulfur terrain.",
          url: "https://en.wikipedia.org/wiki/Pele_(volcano)",
        },
      ],
    },
    {
      stageId: "europa",
      title: "Europa",
      body: "An icy chapter over ridges, bands, and chaos terrain.",
      landmarks: [
        {
          landmarkId: "europa-linea-crossing",
          title: "Europa Linea Crossing",
          atPercent: 31,
          description: "Dark bands crossing a bright ice shell.",
          url: "https://science.nasa.gov/jupiter/moons/europa/",
        },
        {
          landmarkId: "europa-chaos-terrain",
          title: "Chaos Terrain Field",
          atPercent: 42,
          description: "A broken ice landscape of plates, ridges, and refrozen blocks.",
          url: "https://www.nasa.gov/solar-system/newly-reprocessed-images-of-europa-show-chaos-terrain-in-crisp-detail/",
        },
        {
          landmarkId: "europa-ocean-listening-post",
          title: "Ocean Listening Post",
          atPercent: 50,
          description: "A quiet fictional camp inspired by Europa's possible subsurface ocean.",
          url: "https://science.nasa.gov/jupiter/moons/europa/facts/",
        },
      ],
    },
    {
      stageId: "ganymede",
      title: "Ganymede",
      body: "A largest-moon crossing from dark ancient terrain into bright grooved regions.",
      landmarks: [
        {
          landmarkId: "ganymede-galileo-regio",
          title: "Galileo Regio Traverse",
          atPercent: 59,
          description: "A dark ancient region on the largest moon in the Solar System.",
          url: "https://planetarynames.wr.usgs.gov/Feature/2080",
        },
        {
          landmarkId: "ganymede-grooved-terrain",
          title: "Grooved Terrain Ridges",
          atPercent: 67,
          description: "Bright tectonic ridges and lanes across icy ground.",
          url: "https://science.nasa.gov/jupiter/moons/ganymede/",
        },
        {
          landmarkId: "ganymede-magnetic-arch",
          title: "Magnetic Arch Camp",
          atPercent: 75,
          description: "A celebratory stop inspired by Ganymede's magnetic field.",
          url: "https://science.nasa.gov/jupiter/moons/ganymede/facts/",
        },
      ],
    },
    {
      stageId: "callisto",
      title: "Callisto",
      body: "A final outer-moon chapter across ancient cratered terrain.",
      landmarks: [
        {
          landmarkId: "callisto-craterfields",
          title: "Callisto Craterfields",
          atPercent: 83,
          description: "Old cratered highlands on the outermost Galilean moon.",
          url: "https://science.nasa.gov/jupiter/moons/callisto/",
        },
        {
          landmarkId: "callisto-valhalla-rings",
          title: "Valhalla Rings",
          atPercent: 92,
          description: "A wide multi-ring impact basin and the last long traverse.",
          url: "https://planetarynames.wr.usgs.gov/Feature/6308",
        },
        {
          landmarkId: "callisto-grand-tour-finish",
          title: "Callisto Grand Tour Beacon",
          atPercent: 100,
          description: "The final beacon with all four worlds behind you.",
          url: "https://science.nasa.gov/jupiter/moons/callisto/facts/",
        },
      ],
    },
  ],
  rewards: [
    {
      rewardId: "four-moons-sight-io",
      kind: "sight",
      title: "Io Sight",
      unlock: { atPercent: 5 },
      assetId: "four-moons-sight-io-jupiter-rise",
    },
    {
      rewardId: "four-moons-volcanic-compass",
      kind: "chest",
      title: "Volcanic Compass",
      unlock: { atPercent: 33 },
      assetId: "four-moons-item-volcanic-compass",
    },
    {
      rewardId: "four-moons-icebound-crystal",
      kind: "chest",
      title: "Icebound Crystal",
      unlock: { atPercent: 66 },
      assetId: "four-moons-item-icebound-crystal",
    },
    {
      rewardId: "four-moons-jovian-sigil",
      kind: "chest",
      title: "Jovian Sigil",
      unlock: { completion: true },
      assetId: "four-moons-item-jovian-sigil",
    },
    {
      rewardId: "four-moons-complete",
      kind: "badge",
      title: "Four Moons Complete",
      unlock: { completion: true },
      assetId: "four-moons-badge-complete",
    },
  ],
  shareMeta: {
    title: "Four Moons of Jupiter",
    description: "Turn daily movement into a Galilean moons grand tour.",
    ogImageAssetId: "four-moons-hero",
  },
  sourceNotes: [
    "NASA Galilean satellites overview: https://science.nasa.gov/photojournal/the-galilean-satellites/",
    "NASA Jupiter moons overview: https://science.nasa.gov/jupiter/jupiter-moons/",
    "NASA Loki Patera reference: https://science.nasa.gov/photojournal/loki-patera/",
    "NASA Europa chaos terrain reference: https://www.nasa.gov/solar-system/newly-reprocessed-images-of-europa-show-chaos-terrain-in-crisp-detail/",
    "NASA Ganymede overview: https://solarsystem.nasa.gov/moons/jupiter-moons/ganymede/in-depth.amp",
    "NASA Callisto overview: https://solarsystem.nasa.gov/moons/jupiter-moons/callisto/in-depth.amp",
  ],
};

const nationalParksChapters = [
  {
    chapterId: "alaska",
    title: "Alaska",
    badgeRewardId: "national-parks-badge-alaska",
  },
  {
    chapterId: "pacific-west",
    title: "Pacific West",
    badgeRewardId: "national-parks-badge-pacific-west",
  },
  {
    chapterId: "mountain-west",
    title: "Mountain West",
    badgeRewardId: "national-parks-badge-mountain-west",
  },
  {
    chapterId: "southwest",
    title: "Southwest",
    badgeRewardId: "national-parks-badge-southwest",
  },
  {
    chapterId: "midwest",
    title: "Midwest",
    badgeRewardId: "national-parks-badge-midwest",
  },
  {
    chapterId: "east",
    title: "East",
    badgeRewardId: "national-parks-badge-east",
  },
  {
    chapterId: "islands",
    title: "Islands",
    badgeRewardId: "national-parks-badge-islands",
  },
];

const nationalParkCheckpointData = [
  ["acadia", "Acadia", "east"],
  ["american-samoa", "American Samoa", "islands"],
  ["arches", "Arches", "southwest"],
  ["badlands", "Badlands", "midwest"],
  ["big-bend", "Big Bend", "southwest"],
  ["biscayne", "Biscayne", "east"],
  ["black-canyon-of-the-gunnison", "Black Canyon of the Gunnison", "mountain-west"],
  ["bryce-canyon", "Bryce Canyon", "southwest"],
  ["canyonlands", "Canyonlands", "southwest"],
  ["capitol-reef", "Capitol Reef", "southwest"],
  ["carlsbad-caverns", "Carlsbad Caverns", "southwest"],
  ["channel-islands", "Channel Islands", "pacific-west"],
  ["congaree", "Congaree", "east"],
  ["crater-lake", "Crater Lake", "pacific-west"],
  ["cuyahoga-valley", "Cuyahoga Valley", "midwest"],
  ["death-valley", "Death Valley", "pacific-west"],
  ["denali", "Denali", "alaska"],
  ["dry-tortugas", "Dry Tortugas", "east"],
  ["everglades", "Everglades", "east"],
  ["gates-of-the-arctic", "Gates of the Arctic", "alaska"],
  ["gateway-arch", "Gateway Arch", "midwest"],
  ["glacier", "Glacier", "mountain-west"],
  ["glacier-bay", "Glacier Bay", "alaska"],
  ["grand-canyon", "Grand Canyon", "southwest"],
  ["grand-teton", "Grand Teton", "mountain-west"],
  ["great-basin", "Great Basin", "mountain-west"],
  ["great-sand-dunes", "Great Sand Dunes", "mountain-west"],
  ["great-smoky-mountains", "Great Smoky Mountains", "east"],
  ["guadalupe-mountains", "Guadalupe Mountains", "southwest"],
  ["haleakala", "Haleakala", "pacific-west"],
  ["hawaii-volcanoes", "Hawaii Volcanoes", "pacific-west"],
  ["hot-springs", "Hot Springs", "southwest"],
  ["indiana-dunes", "Indiana Dunes", "midwest"],
  ["isle-royale", "Isle Royale", "midwest"],
  ["joshua-tree", "Joshua Tree", "pacific-west"],
  ["katmai", "Katmai", "alaska"],
  ["kenai-fjords", "Kenai Fjords", "alaska"],
  ["kings-canyon", "Kings Canyon", "pacific-west"],
  ["kobuk-valley", "Kobuk Valley", "alaska"],
  ["lake-clark", "Lake Clark", "alaska"],
  ["lassen-volcanic", "Lassen Volcanic", "pacific-west"],
  ["mammoth-cave", "Mammoth Cave", "east"],
  ["mesa-verde", "Mesa Verde", "southwest"],
  ["mount-rainier", "Mount Rainier", "pacific-west"],
  ["new-river-gorge", "New River Gorge", "east"],
  ["north-cascades", "North Cascades", "pacific-west"],
  ["olympic", "Olympic", "pacific-west"],
  ["petrified-forest", "Petrified Forest", "southwest"],
  ["pinnacles", "Pinnacles", "pacific-west"],
  ["redwood", "Redwood", "pacific-west"],
  ["rocky-mountain", "Rocky Mountain", "mountain-west"],
  ["saguaro", "Saguaro", "southwest"],
  ["sequoia", "Sequoia", "pacific-west"],
  ["shenandoah", "Shenandoah", "east"],
  ["theodore-roosevelt", "Theodore Roosevelt", "midwest"],
  ["virgin-islands", "Virgin Islands", "islands"],
  ["voyageurs", "Voyageurs", "midwest"],
  ["white-sands", "White Sands", "southwest"],
  ["wind-cave", "Wind Cave", "midwest"],
  ["wrangell-st-elias", "Wrangell-St. Elias", "alaska"],
  ["yellowstone", "Yellowstone", "mountain-west"],
  ["yosemite", "Yosemite", "pacific-west"],
  ["zion", "Zion", "southwest"],
] as const;

const NATIONAL_PARKS_GAME_DISTANCE_METERS = milesToMeters(630);

export const nationalParksGrandTourRoute: LinearQuestRoute = {
  routeId: "national-parks-grand-tour",
  routeVersionId: "national-parks-grand-tour-63-v1",
  schemaVersion: 1,
  title: "National Parks Grand Tour",
  description:
    "A walking-powered quest across the 63 U.S. sites officially designated as national parks, with a small park-themed chest at each stop.",
  kind: "linear_route",
  status: "planned",
  progressMetric: "distance",
  gameDistanceMeters: NATIONAL_PARKS_GAME_DISTANCE_METERS,
  realDistanceNote:
    "Scaled walking quest. Parks are alphabetical route milestones, not a literal road trip or continuous trail.",
  displayDistanceMode: "scaled_distance",
  renderMode: "static_map",
  progressPolicy: {
    movementSource: "distance",
    strideMeters: 0.762,
    gameDistanceMeters: NATIONAL_PARKS_GAME_DISTANCE_METERS,
    displayDistanceMode: "scaled_distance",
  },
  assets: [
    {
      assetId: "national-parks-grand-tour-hero",
      kind: "hero",
      path: "/assets/quests/national-parks-grand-tour/hero.png",
    },
    {
      assetId: "national-parks-grand-tour-sight-acadia",
      kind: "sight",
      path: "/assets/quests/national-parks-grand-tour/sight-acadia.png",
    },
    {
      assetId: "national-parks-grand-tour-item-park-chest",
      kind: "item",
      path: "/assets/quests/national-parks-grand-tour/item-park-chest.png",
    },
    {
      assetId: "national-parks-grand-tour-badge-regional-parks",
      kind: "badge",
      path: "/assets/quests/national-parks-grand-tour/badge-regional-parks.png",
    },
    {
      assetId: "national-parks-grand-tour-badge-grand-passport",
      kind: "badge",
      path: "/assets/quests/national-parks-grand-tour/badge-grand-passport.png",
    },
  ],
  landmarks: nationalParkCheckpointData.map(
    ([parkId, title, chapterId], index) => ({
      landmarkId: `national-park-${parkId}`,
      title,
      atMeters: nationalParkStopMeters(index),
      description: `${title} route stop in the ${chapterTitle(chapterId)} chapter.`,
      url: nationalParkUrl(title),
      ...(index === 0
        ? { assetId: "national-parks-grand-tour-sight-acadia" }
        : {}),
    }),
  ),
  rewards: [
    ...nationalParkCheckpointData.map(([parkId, title], index) => ({
      rewardId: `national-parks-chest-${parkId}`,
      kind: "chest" as const,
      title: `${title} Chest`,
      unlock: { atMeters: nationalParkStopMeters(index) },
      assetId: "national-parks-grand-tour-item-park-chest",
    })),
    ...nationalParksChapters.map((chapter) => ({
      rewardId: chapter.badgeRewardId,
      kind: "badge" as const,
      title: `${chapter.title} Parks Badge`,
      unlock: { atMeters: chapterCompleteMeters(chapter.chapterId) },
      assetId: "national-parks-grand-tour-badge-regional-parks",
    })),
    {
      rewardId: "national-parks-grand-passport",
      kind: "badge",
      title: "Grand Parks Passport",
      unlock: { completion: true },
      assetId: "national-parks-grand-tour-badge-grand-passport",
    },
  ],
  shareMeta: {
    title: "National Parks Grand Tour",
    description: "Turn daily movement into a 63-park chest-and-badge quest.",
    ogImageAssetId: "national-parks-grand-tour-hero",
  },
  sourceNotes: [
    "NPS National Park System page lists National Parks (63): https://www.nps.gov/aboutus/national-park-system.htm/index.htm",
    "National Park Foundation explains that 63 sites carry the national park designation: https://www.nationalparks.org/americas-national-parks",
    "This is a walking-powered route with park-themed chests and not a literal road trip; park ordering is alphabetical for v1 stability.",
    "Future route preferences should let users choose or reshuffle park order while keeping all progress step/distance-driven.",
  ],
};

const earthCityTourChapters = [
  { chapterId: "northeast", title: "Northeast", badgeRewardId: "earth-city-badge-northeast" },
  { chapterId: "southeast", title: "Southeast", badgeRewardId: "earth-city-badge-southeast" },
  { chapterId: "midwest", title: "Midwest", badgeRewardId: "earth-city-badge-midwest" },
  { chapterId: "southwest", title: "Southwest", badgeRewardId: "earth-city-badge-southwest" },
  { chapterId: "mountain-west", title: "Mountain West", badgeRewardId: "earth-city-badge-mountain-west" },
  { chapterId: "pacific-west", title: "Pacific West", badgeRewardId: "earth-city-badge-pacific-west" },
] as const;

const earthCityWaypointData = [
  ["birmingham-al", "Birmingham, Alabama", "southeast"],
  ["montgomery-al", "Montgomery, Alabama", "southeast"],
  ["anchorage-ak", "Anchorage, Alaska", "pacific-west"],
  ["fairbanks-ak", "Fairbanks, Alaska", "pacific-west"],
  ["phoenix-az", "Phoenix, Arizona", "southwest"],
  ["tucson-az", "Tucson, Arizona", "southwest"],
  ["little-rock-ar", "Little Rock, Arkansas", "southeast"],
  ["fayetteville-ar", "Fayetteville, Arkansas", "southeast"],
  ["los-angeles-ca", "Los Angeles, California", "pacific-west"],
  ["san-francisco-ca", "San Francisco, California", "pacific-west"],
  ["denver-co", "Denver, Colorado", "mountain-west"],
  ["colorado-springs-co", "Colorado Springs, Colorado", "mountain-west"],
  ["bridgeport-ct", "Bridgeport, Connecticut", "northeast"],
  ["hartford-ct", "Hartford, Connecticut", "northeast"],
  ["wilmington-de", "Wilmington, Delaware", "northeast"],
  ["dover-de", "Dover, Delaware", "northeast"],
  ["jacksonville-fl", "Jacksonville, Florida", "southeast"],
  ["miami-fl", "Miami, Florida", "southeast"],
  ["atlanta-ga", "Atlanta, Georgia", "southeast"],
  ["savannah-ga", "Savannah, Georgia", "southeast"],
  ["honolulu-hi", "Honolulu, Hawaii", "pacific-west"],
  ["hilo-hi", "Hilo, Hawaii", "pacific-west"],
  ["boise-id", "Boise, Idaho", "mountain-west"],
  ["idaho-falls-id", "Idaho Falls, Idaho", "mountain-west"],
  ["chicago-il", "Chicago, Illinois", "midwest"],
  ["springfield-il", "Springfield, Illinois", "midwest"],
  ["indianapolis-in", "Indianapolis, Indiana", "midwest"],
  ["fort-wayne-in", "Fort Wayne, Indiana", "midwest"],
  ["des-moines-ia", "Des Moines, Iowa", "midwest"],
  ["cedar-rapids-ia", "Cedar Rapids, Iowa", "midwest"],
  ["wichita-ks", "Wichita, Kansas", "midwest"],
  ["topeka-ks", "Topeka, Kansas", "midwest"],
  ["louisville-ky", "Louisville, Kentucky", "southeast"],
  ["lexington-ky", "Lexington, Kentucky", "southeast"],
  ["new-orleans-la", "New Orleans, Louisiana", "southeast"],
  ["baton-rouge-la", "Baton Rouge, Louisiana", "southeast"],
  ["portland-me", "Portland, Maine", "northeast"],
  ["bangor-me", "Bangor, Maine", "northeast"],
  ["baltimore-md", "Baltimore, Maryland", "northeast"],
  ["annapolis-md", "Annapolis, Maryland", "northeast"],
  ["boston-ma", "Boston, Massachusetts", "northeast"],
  ["worcester-ma", "Worcester, Massachusetts", "northeast"],
  ["detroit-mi", "Detroit, Michigan", "midwest"],
  ["grand-rapids-mi", "Grand Rapids, Michigan", "midwest"],
  ["minneapolis-mn", "Minneapolis, Minnesota", "midwest"],
  ["duluth-mn", "Duluth, Minnesota", "midwest"],
  ["jackson-ms", "Jackson, Mississippi", "southeast"],
  ["gulfport-ms", "Gulfport, Mississippi", "southeast"],
  ["kansas-city-mo", "Kansas City, Missouri", "midwest"],
  ["st-louis-mo", "St. Louis, Missouri", "midwest"],
  ["billings-mt", "Billings, Montana", "mountain-west"],
  ["missoula-mt", "Missoula, Montana", "mountain-west"],
  ["omaha-ne", "Omaha, Nebraska", "midwest"],
  ["lincoln-ne", "Lincoln, Nebraska", "midwest"],
  ["las-vegas-nv", "Las Vegas, Nevada", "southwest"],
  ["reno-nv", "Reno, Nevada", "southwest"],
  ["manchester-nh", "Manchester, New Hampshire", "northeast"],
  ["concord-nh", "Concord, New Hampshire", "northeast"],
  ["newark-nj", "Newark, New Jersey", "northeast"],
  ["jersey-city-nj", "Jersey City, New Jersey", "northeast"],
  ["albuquerque-nm", "Albuquerque, New Mexico", "southwest"],
  ["santa-fe-nm", "Santa Fe, New Mexico", "southwest"],
  ["new-york-city-ny", "New York City, New York", "northeast"],
  ["buffalo-ny", "Buffalo, New York", "northeast"],
  ["charlotte-nc", "Charlotte, North Carolina", "southeast"],
  ["raleigh-nc", "Raleigh, North Carolina", "southeast"],
  ["fargo-nd", "Fargo, North Dakota", "midwest"],
  ["bismarck-nd", "Bismarck, North Dakota", "midwest"],
  ["columbus-oh", "Columbus, Ohio", "midwest"],
  ["cleveland-oh", "Cleveland, Ohio", "midwest"],
  ["oklahoma-city-ok", "Oklahoma City, Oklahoma", "southwest"],
  ["tulsa-ok", "Tulsa, Oklahoma", "southwest"],
  ["portland-or", "Portland, Oregon", "pacific-west"],
  ["eugene-or", "Eugene, Oregon", "pacific-west"],
  ["philadelphia-pa", "Philadelphia, Pennsylvania", "northeast"],
  ["pittsburgh-pa", "Pittsburgh, Pennsylvania", "northeast"],
  ["providence-ri", "Providence, Rhode Island", "northeast"],
  ["newport-ri", "Newport, Rhode Island", "northeast"],
  ["charleston-sc", "Charleston, South Carolina", "southeast"],
  ["columbia-sc", "Columbia, South Carolina", "southeast"],
  ["sioux-falls-sd", "Sioux Falls, South Dakota", "midwest"],
  ["rapid-city-sd", "Rapid City, South Dakota", "midwest"],
  ["nashville-tn", "Nashville, Tennessee", "southeast"],
  ["memphis-tn", "Memphis, Tennessee", "southeast"],
  ["houston-tx", "Houston, Texas", "southwest"],
  ["dallas-tx", "Dallas, Texas", "southwest"],
  ["salt-lake-city-ut", "Salt Lake City, Utah", "mountain-west"],
  ["provo-ut", "Provo, Utah", "mountain-west"],
  ["burlington-vt", "Burlington, Vermont", "northeast"],
  ["montpelier-vt", "Montpelier, Vermont", "northeast"],
  ["virginia-beach-va", "Virginia Beach, Virginia", "southeast"],
  ["richmond-va", "Richmond, Virginia", "southeast"],
  ["seattle-wa", "Seattle, Washington", "pacific-west"],
  ["spokane-wa", "Spokane, Washington", "pacific-west"],
  ["charleston-wv", "Charleston, West Virginia", "southeast"],
  ["morgantown-wv", "Morgantown, West Virginia", "southeast"],
  ["milwaukee-wi", "Milwaukee, Wisconsin", "midwest"],
  ["madison-wi", "Madison, Wisconsin", "midwest"],
  ["cheyenne-wy", "Cheyenne, Wyoming", "mountain-west"],
  ["casper-wy", "Casper, Wyoming", "mountain-west"],
] as const;

const EARTH_CITY_TOUR_GAME_DISTANCE_METERS = milesToMeters(1_000);

export const earthCityTourRoute: LinearQuestRoute = {
  routeId: "earth-city-tour",
  routeVersionId: "earth-city-tour-us-100-v1",
  schemaVersion: 1,
  title: "Earth City Tour",
  description:
    "A walking-powered tour through two large or recognizable cities in every U.S. state, with a chest at each city stop.",
  kind: "linear_route",
  status: "planned",
  progressMetric: "distance",
  gameDistanceMeters: EARTH_CITY_TOUR_GAME_DISTANCE_METERS,
  realDistanceNote:
    "Scaled walking quest. Cities are curated waypoints with a deterministic default order; future versions should let users choose the next city or use custom city stops.",
  displayDistanceMode: "scaled_distance",
  renderMode: "static_map",
  progressPolicy: {
    movementSource: "distance",
    strideMeters: 0.762,
    gameDistanceMeters: EARTH_CITY_TOUR_GAME_DISTANCE_METERS,
    displayDistanceMode: "scaled_distance",
  },
  assets: [
    {
      assetId: "earth-city-tour-hero",
      kind: "hero",
      path: "/assets/quests/earth-city-tour/hero.png",
    },
    {
      assetId: "earth-city-tour-sight-birmingham",
      kind: "sight",
      path: "/assets/quests/earth-city-tour/sight-birmingham.png",
    },
    {
      assetId: "earth-city-tour-item-city-chest",
      kind: "item",
      path: "/assets/quests/earth-city-tour/item-city-chest.png",
    },
    {
      assetId: "earth-city-tour-badge-regional-cities",
      kind: "badge",
      path: "/assets/quests/earth-city-tour/badge-regional-cities.png",
    },
    {
      assetId: "earth-city-tour-badge-complete",
      kind: "badge",
      path: "/assets/quests/earth-city-tour/badge-complete.png",
    },
  ],
  landmarks: earthCityWaypointData.map(([cityId, title, chapterId], index) => ({
    landmarkId: `earth-city-${cityId}`,
    title,
    atMeters: earthCityStopMeters(index),
    description: `${title} city stop in the ${earthCityChapterTitle(chapterId)} chapter.`,
    url: wikipediaUrl(title),
    ...(index === 0 ? { assetId: "earth-city-tour-sight-birmingham" } : {}),
  })),
  rewards: [
    ...earthCityWaypointData.map(([cityId, title], index) => ({
      rewardId: `earth-city-chest-${cityId}`,
      kind: "chest" as const,
      title: `${title} Chest`,
      unlock: { atMeters: earthCityStopMeters(index) },
      assetId: "earth-city-tour-item-city-chest",
    })),
    ...earthCityTourChapters.map((chapter) => ({
      rewardId: chapter.badgeRewardId,
      kind: "badge" as const,
      title: `${chapter.title} Cities Badge`,
      unlock: { atMeters: earthCityChapterCompleteMeters(chapter.chapterId) },
      assetId: "earth-city-tour-badge-regional-cities",
    })),
    {
      rewardId: "earth-city-tour-complete",
      kind: "badge",
      title: "Earth City Tour Complete",
      unlock: { completion: true },
      assetId: "earth-city-tour-badge-complete",
    },
  ],
  shareMeta: {
    title: "Earth City Tour",
    description: "Turn daily movement into a curated U.S. city waypoint quest.",
    ogImageAssetId: "earth-city-tour-hero",
  },
  sourceNotes: [
    "V1 uses a curated finite list of two large or recognizable cities per U.S. state for stable generated assets and tests.",
    "Progress is always step/distance-driven; city order is default route data, not a requirement for real-world travel.",
    "Future versions should support choosing the next city from the curated list and custom cities with generic city assets.",
  ],
};

const UNDER_THE_SEA_GAME_DISTANCE_METERS = milesToMeters(1_200);

const underTheSeaWaypointData = [
  {
    id: "florida-keys-reef",
    title: "Florida Keys Reef Tract",
    chapter: "Sunlit Shallows",
    lat: 24.55,
    lon: -81.78,
    blurb:
      "NOAA's Florida Keys sanctuary protects North America's only continental coral barrier reef ecosystem.",
    quip: "Parrotfish painted the mile marker and left a tiny cloud of sand.",
    item: "Seagrass Charm",
  },
  {
    id: "great-blue-hole",
    title: "Great Blue Hole",
    chapter: "Sunlit Shallows",
    lat: 17.316,
    lon: -87.535,
    blurb:
      "A giant marine sinkhole near Lighthouse Reef, visible from space in NASA imagery.",
    quip: "Nurse sharks call it the deep end and politely keep score.",
    item: "Blue Hole Pearl",
  },
  {
    id: "puerto-rico-trench",
    title: "Puerto Rico Trench",
    chapter: "Atlantic Descent",
    lat: 19.5833,
    lon: -68.3417,
    blurb:
      "The Atlantic's deepest trench system, including Milwaukee Deep at more than 8,300 meters.",
    quip: "An anglerfish brought the flashlight and asked everyone to admire it.",
    item: "Trenchlight Lure",
  },
  {
    id: "hudson-canyon",
    title: "Hudson Canyon",
    chapter: "Whale Roads",
    lat: 39.5,
    lon: -72.5,
    blurb:
      "The largest submarine canyon along the U.S. Atlantic coast, now proposed as a national marine sanctuary.",
    quip: "The canyon made a grand entrance and the whales approved the acoustics.",
    item: "Canyon Echo Shell",
  },
  {
    id: "stellwagen-bank",
    title: "Stellwagen Bank",
    chapter: "Whale Roads",
    lat: 42.33,
    lon: -70.28,
    blurb:
      "A shallow offshore bank and national marine sanctuary at Massachusetts Bay.",
    quip: "A humpback surfaced for applause and absolutely got it.",
    item: "Whalesong Conch",
  },
  {
    id: "mid-atlantic-ridge",
    title: "Mid-Atlantic Ridge",
    chapter: "Atlantic Rift",
    lat: 65,
    lon: -24.85,
    blurb:
      "A mostly underwater mountain range running through the Atlantic Ocean.",
    quip: "The seahorses prefer the scenic ridge route, naturally.",
    item: "Ridge Current Charm",
  },
  {
    id: "romanche-passage",
    title: "Romanche Passage",
    chapter: "Atlantic Rift",
    lat: -0.166667,
    lon: -18.25,
    blurb:
      "A deep equatorial Atlantic passage through the Mid-Atlantic Ridge system.",
    quip: "A lanternfish checked the compass and declared the equator delightfully dramatic.",
    item: "Equator Lantern",
  },
  {
    id: "south-sandwich-trench",
    title: "South Sandwich Trench",
    chapter: "Southern Deep",
    lat: -56.5,
    lon: -25,
    blurb:
      "A remote trench system in the southernmost Atlantic and Southern Ocean region.",
    quip: "A tiny amphipod waved from somewhere extremely far below.",
    item: "Southern Deep Shard",
  },
  {
    id: "walvis-ridge",
    title: "Walvis Ridge",
    chapter: "South Atlantic Crossing",
    lat: -32.958,
    lon: -5.22,
    blurb:
      "A South Atlantic undersea ridge extending from the Namibian margin toward Tristan da Cunha.",
    quip: "A lantern shark offered directions with unnecessary gravitas.",
    item: "Lantern Shark Scale",
  },
  {
    id: "agulhas-ridge",
    title: "Agulhas Ridge",
    chapter: "South Atlantic Crossing",
    lat: -42.68155343,
    lon: 9.76942228,
    blurb:
      "A GEBCO-named ridge south of Africa near the Atlantic and Indian Ocean gateway.",
    quip: "The currents argued politely, then all flowed onward.",
    item: "Current-Knot Compass",
  },
  {
    id: "aldabra-atoll",
    title: "Aldabra Atoll",
    chapter: "Indian Ocean Gardens",
    lat: -9.42,
    lon: 46.348,
    blurb:
      "A remote Seychelles atoll with coral reef, shallow lagoon, and long-protected island ecosystems.",
    quip: "Turtle traffic has right of way, and the turtles know it.",
    item: "Atoll Tortoise Medallion",
  },
  {
    id: "kerguelen-plateau",
    title: "Kerguelen Plateau",
    chapter: "Indian Ocean Gardens",
    lat: -54.74973833,
    lon: 74.40059403,
    blurb:
      "A vast submarine plateau in the southern Indian Ocean, mapped in the GEBCO gazetteer.",
    quip: "A whale took the scenic plateau lane and hummed a victory chord.",
    item: "Plateau Whale Chime",
  },
  {
    id: "ninetyeast-ridge",
    title: "Ninetyeast Ridge",
    chapter: "Indian Ocean Gardens",
    lat: -13.48405428,
    lon: 88.46746367,
    blurb:
      "A long linear ridge running near the 90th meridian through the Indian Ocean.",
    quip: "The ridge ran so straight that even the squid respected the line.",
    item: "Meridian Ridge Needle",
  },
  {
    id: "sunda-trench",
    title: "Sunda Trench",
    chapter: "Indian Ocean Gardens",
    lat: -9.315193,
    lon: 108.905716,
    blurb:
      "Also called the Java Trench, this is the Indian Ocean's hadal trench system south of Java and Sumatra.",
    quip: "The deep-sea cucumber signed the guestbook very slowly.",
    item: "Hadal Cucumber Charm",
  },
  {
    id: "tubbataha-reefs",
    title: "Tubbataha Reefs Natural Park",
    chapter: "Reef Constellation",
    lat: 8.9,
    lon: 119.88,
    blurb:
      "A Coral Triangle World Heritage site with hundreds of coral and fish species.",
    quip: "The reef shark patrol arrived exactly on schedule.",
    item: "Wrasse Crown",
  },
  {
    id: "challenger-deep",
    title: "Challenger Deep",
    chapter: "Abyssal Descent",
    lat: 11.373,
    lon: 142.592,
    blurb:
      "The deepest known point in Earth's ocean, at the southwest end of the Mariana Trench.",
    quip: "The pressure was so high even the crabs whispered.",
    item: "Abyssal Pressure Core",
  },
  {
    id: "great-barrier-reef",
    title: "Great Barrier Reef",
    chapter: "Reef Constellation",
    lat: -18.29,
    lon: 147.7,
    blurb:
      "The world's most extensive coral reef ecosystem, inscribed by UNESCO in 1981.",
    quip: "A dugong reviewed the route and recommended the seagrass lane.",
    item: "Barrier Bloom",
  },
  {
    id: "phoenix-islands",
    title: "Phoenix Islands Protected Area",
    chapter: "Deep and Far",
    lat: -3.822,
    lon: -172.738,
    blurb:
      "A large Pacific protected area with coral archipelago and seamount habitats.",
    quip: "A manta ray took the long glide and made it look easy.",
    item: "Manta Drift Prism",
  },
  {
    id: "papahanaumokuakea",
    title: "Papahanaumokuakea",
    chapter: "Deep and Far",
    lat: 25.7,
    lon: -171.73,
    blurb:
      "A vast protected Northwestern Hawaiian Islands seascape extending roughly 1,931 kilometers.",
    quip: "A monk seal napped like it had already completed the daily quest.",
    item: "Monk Seal Sigil",
  },
  {
    id: "kamaehuakanaloa",
    title: "Kama'ehuakanaloa Seamount",
    chapter: "Volcanic Blue",
    lat: 18.92,
    lon: -155.27,
    blurb:
      "An active submarine volcano south of Kilauea, formerly known as Loihi Seamount.",
    quip: "Baby volcano, big splash energy.",
    item: "Young Volcano Sigil",
  },
  {
    id: "axial-seamount",
    title: "Axial Seamount",
    chapter: "Volcanic Blue",
    lat: 45.945,
    lon: -129.937,
    blurb:
      "The most active submarine volcano in the northeast Pacific, west of Oregon.",
    quip: "An octopus volcanologist took notes with all available arms.",
    item: "Basalt Bubble Lens",
  },
  {
    id: "davidson-seamount",
    title: "Davidson Seamount",
    chapter: "Kelp and Canyons",
    lat: 35.717,
    lon: -122.717,
    blurb:
      "A protected seamount southwest of Monterey known as a well-explored deep-sea habitat.",
    quip: "The coral garden needed no lawn mower and preferred it that way.",
    item: "Coral Lantern",
  },
  {
    id: "monterey-canyon",
    title: "Monterey Canyon",
    chapter: "Kelp and Canyons",
    lat: 36.667,
    lon: -122.083,
    blurb:
      "A submarine canyon beginning near Moss Landing and traceable hundreds of kilometers offshore.",
    quip: "A kelp bass said to mind the drop, then vanished into excellent lighting.",
    item: "Kelpweave Cloak",
  },
  {
    id: "east-pacific-rise",
    title: "East Pacific Rise",
    chapter: "Volcanic Blue",
    lat: 9.83,
    lon: -104.29,
    blurb:
      "A heavily studied fast-spreading mid-ocean ridge segment near 9 to 10 degrees north.",
    quip: "The black smokers were busy, but the shrimp made room.",
    item: "Black Smoker Ember",
  },
  {
    id: "galapagos-rift-vents",
    title: "Galapagos Rift Vents",
    chapter: "Volcanic Blue",
    lat: 0.75,
    lon: -85.833,
    blurb:
      "The hydrothermal-vent region where unusual vent organisms were first found in 1977.",
    quip: "Tube worms found a loophole in sunlight-based living.",
    item: "Chemosynthesis Rune",
  },
  {
    id: "galapagos-marine-reserve",
    title: "Galapagos Marine Reserve",
    chapter: "Blue Planet Finale",
    lat: -0.065,
    lon: -90.765,
    blurb:
      "A major marine extension around the Galapagos, a globally important living museum of evolution.",
    quip: "A marine iguana took the warm-up lap and looked very serious.",
    item: "Iguana Current Stone",
  },
] as const;

const underTheSeaWaypointMeta = {
  "florida-keys-reef": {
    url: "https://floridakeys.noaa.gov/corals/coralreefs.html",
    feature: "reef garden",
    wildlife: ["a parrotfish", "a green sea turtle", "a school of blue tangs", "a queen angelfish", "a nurse shark"],
  },
  "great-blue-hole": {
    url: "https://whc.unesco.org/en/list/764/",
    feature: "blue sinkhole",
    wildlife: ["a nurse shark", "a reef shark", "a midnight parrotfish", "a Caribbean reef squid", "a horse-eye jack"],
  },
  "puerto-rico-trench": {
    url: "https://pubs.usgs.gov/publication/ofr20131125",
    feature: "deep blue drop",
    wildlife: ["a hadal amphipod", "a lanternfish", "a deep-sea holothurian", "a cusk eel", "a tripod fish"],
  },
  "hudson-canyon": {
    url: "https://sanctuaries.noaa.gov/hudson-canyon/",
    feature: "canyon wall",
    wildlife: ["a sperm whale", "a loggerhead sea turtle", "a deepwater coral crab", "a tilefish", "a cold-water coral polyp"],
  },
  "stellwagen-bank": {
    url: "https://stellwagen.noaa.gov/",
    feature: "shallow bank",
    wildlife: ["a humpback whale", "a sand lance school", "a North Atlantic right whale", "a minke whale", "an Atlantic cod"],
  },
  "mid-atlantic-ridge": {
    url: "https://whc.unesco.org/en/activities/504/",
    feature: "ridge line",
    wildlife: ["a deep-sea shrimp", "a lanternfish", "a curious squid", "a grenadier", "a black coral branch"],
  },
  "romanche-passage": {
    url: "https://marineregions.org/gazetteer.php?id=6704&p=details",
    feature: "equatorial deep passage",
    wildlife: ["a lanternfish", "a hatchetfish", "a glass squid", "a viperfish", "a deep-sea jelly"],
  },
  "south-sandwich-trench": {
    url: "https://www.marineregions.org/mrgid/6753",
    feature: "southern trench",
    wildlife: ["a hadal amphipod", "a deep-sea snailfish", "a patient brittle star", "an Antarctic toothfish", "a deep-sea isopod"],
  },
  "walvis-ridge": {
    url: "https://marineregions.org/gazetteer.php?id=7177&p=details",
    feature: "South Atlantic ridge",
    wildlife: ["a lantern shark", "a pilot whale", "a silver school of fish", "a deepwater squid", "a Cape hake"],
  },
  "agulhas-ridge": {
    url: "https://www.marineregions.org/gazetteer.php?id=7173&p=details",
    feature: "current-washed ridge",
    wildlife: ["a blue shark", "a lanternfish", "a silver school of fish", "a squid in the Agulhas current", "a deepwater hake"],
  },
  "aldabra-atoll": {
    url: "https://whc.unesco.org/en/list/185/",
    feature: "atoll lagoon",
    wildlife: ["a green sea turtle", "a manta ray", "a blacktip reef shark", "a hawksbill turtle", "a reef grouper"],
  },
  "kerguelen-plateau": {
    url: "https://www.marineregions.org/gazetteer.php?id=7269&p=details",
    feature: "submerged plateau",
    wildlife: ["a southern elephant seal", "a lanternfish", "a squid in the cold current", "a Patagonian toothfish", "a deep-sea brittle star"],
  },
  "ninetyeast-ridge": {
    url: "https://marineregions.org/mrgid/7894",
    feature: "meridian ridge",
    wildlife: ["a deepwater squid", "a lanternfish", "a silky shark", "a tuna school", "a deep-sea dragonfish"],
  },
  "sunda-trench": {
    url: "https://www.marineregions.org/gazetteer.php?id=7327&p=details",
    feature: "hadal trench",
    wildlife: ["a hadal amphipod", "a deep-sea cucumber", "a snailfish", "a deep-sea shrimp", "a brittle star"],
  },
  "tubbataha-reefs": {
    url: "https://whc.unesco.org/en/list/653/",
    feature: "Coral Triangle reef",
    wildlife: ["a reef shark", "a Napoleon wrasse", "a green sea turtle", "a manta ray", "a school of jacks"],
  },
  "challenger-deep": {
    url: "https://oceanservice.noaa.gov/facts/oceandepth.html",
    feature: "deepest known ocean point",
    wildlife: ["a hadal amphipod", "a deep-sea cucumber", "a tiny snailfish", "a deep-sea isopod", "a xenophyophore"],
  },
  "great-barrier-reef": {
    url: "https://whc.unesco.org/en/list/154/",
    feature: "vast coral reef",
    wildlife: ["a dugong", "a clownfish", "a sea turtle", "a giant clam", "a coral trout"],
  },
  "phoenix-islands": {
    url: "https://whc.unesco.org/en/list/1325/",
    feature: "remote Pacific seascape",
    wildlife: ["a manta ray", "a grey reef shark", "a school of tuna", "a parrotfish", "a reef triggerfish"],
  },
  papahanaumokuakea: {
    url: "https://www.papahanaumokuakea.gov/",
    feature: "protected island seascape",
    wildlife: ["a Hawaiian monk seal", "a green sea turtle", "a spinner dolphin", "a masked angelfish", "a reef manta ray"],
  },
  kamaehuakanaloa: {
    url: "https://www.usgs.gov/volcanoes/kama%E2%80%98ehuakanaloa",
    feature: "young submarine volcano",
    wildlife: ["a deep-sea shrimp", "a wandering octopus", "a lanternfish", "a squat lobster", "a glass sponge"],
  },
  "axial-seamount": {
    url: "https://www.usgs.gov/observatories/cvo/science/axial-seamount",
    feature: "active submarine volcano",
    wildlife: ["a vent shrimp", "a deep-sea crab", "a curious octopus", "a tubeworm cluster", "a vent limpet"],
  },
  "davidson-seamount": {
    url: "https://montereybay.noaa.gov/science/characterization/davidson-seamount.html",
    feature: "coral-covered seamount",
    wildlife: ["a deep-sea coral crab", "a bubblegum coral colony", "a passing jelly", "a rockfish", "a basket star"],
  },
  "monterey-canyon": {
    url: "https://montereybay.noaa.gov/",
    feature: "submarine canyon",
    wildlife: ["a market squid", "a deepwater rockfish", "a comb jelly", "a giant Pacific octopus", "a sablefish"],
  },
  "east-pacific-rise": {
    url: "https://volcano.si.edu/volcano.cfm?vn=334050",
    feature: "fast-spreading ridge",
    wildlife: ["a vent shrimp", "a tubeworm cluster", "a deep-sea crab", "a vent mussel", "a zoarcid fish"],
  },
  "galapagos-rift-vents": {
    url: "https://www.ncei.noaa.gov/access/metadata/landing-page/bin/iso?id=gov.noaa.ncei%3AGalapagosRift2005",
    feature: "hydrothermal vent field",
    wildlife: ["a giant tubeworm", "a vent crab", "a vent mussel cluster", "a white shrimp", "a deep-sea clam"],
  },
  "galapagos-marine-reserve": {
    url: "https://whc.unesco.org/en/list/1/",
    feature: "evolutionary marine reserve",
    wildlife: ["a marine iguana", "a scalloped hammerhead", "a Galapagos sea lion", "a green sea turtle", "a spotted eagle ray"],
  },
} satisfies Record<
  (typeof underTheSeaWaypointData)[number]["id"],
  { url: string; feature: string; wildlife: string[] }
>;

export const underTheSeaRoute: LinearQuestRoute = {
  routeId: "under-the-sea",
  routeVersionId: "under-the-sea-world-oceans-v1",
  schemaVersion: 1,
  title: "Under the Sea",
  description:
    "A Blue Atlas Expedition through reefs, kelp forests, seamounts, vents, trenches, and bright ocean-life encounters.",
  kind: "linear_route",
  status: "enabled",
  progressMetric: "distance",
  gameDistanceMeters: UNDER_THE_SEA_GAME_DISTANCE_METERS,
  realDistanceNote:
    "Scaled walking quest. Waypoints are real ocean places and representative coordinates, but the route line is a symbolic expedition path, not a vessel track, dive plan, or navigation route.",
  displayDistanceMode: "scaled_distance",
  renderMode: "static_map",
  progressPolicy: {
    movementSource: "distance",
    strideMeters: 0.762,
    gameDistanceMeters: UNDER_THE_SEA_GAME_DISTANCE_METERS,
    displayDistanceMode: "scaled_distance",
  },
  assets: [
    {
      assetId: "under-the-sea-hero",
      kind: "hero",
      path: "/assets/quests/under-the-sea/hero.png",
    },
    {
      assetId: "under-the-sea-map-noaa-etopo",
      kind: "map",
      path: "/assets/quests/under-the-sea/map-noaa-etopo-global-relief.jpg",
    },
    {
      assetId: "under-the-sea-sight-florida-keys",
      kind: "sight",
      path: "/assets/quests/under-the-sea/sight-florida-keys.png",
    },
    {
      assetId: "under-the-sea-item-conch-compass",
      kind: "item",
      path: "/assets/quests/under-the-sea/item-conch-compass.png",
    },
    {
      assetId: "under-the-sea-item-chemosynthesis-rune",
      kind: "item",
      path: "/assets/quests/under-the-sea/item-chemosynthesis-rune.png",
    },
    {
      assetId: "under-the-sea-item-abyssal-pressure-core",
      kind: "item",
      path: "/assets/quests/under-the-sea/item-abyssal-pressure-core.png",
    },
    {
      assetId: "under-the-sea-badge-blue-atlas",
      kind: "badge",
      path: "/assets/quests/under-the-sea/badge-blue-atlas.png",
    },
  ],
  landmarks: underTheSeaWaypointData.map((waypoint, index) => {
    const meta = underTheSeaWaypointMeta[waypoint.id];
    return {
      landmarkId: `under-sea-${waypoint.id}`,
      title: waypoint.title,
      atMeters: underTheSeaStopMeters(index),
      description: `${waypoint.blurb} ${waypoint.quip}`,
      url: meta.url,
      coordinates: { lat: waypoint.lat, lon: waypoint.lon },
      journal: { feature: meta.feature, wildlife: meta.wildlife },
      ...(index === 0 ? { assetId: "under-the-sea-sight-florida-keys" } : {}),
    };
  }),
  rewards: [
    {
      rewardId: "under-the-sea-sight-florida-keys",
      kind: "sight",
      title: "Florida Keys Reef Sight",
      unlock: { atMeters: underTheSeaStopMeters(0) },
      assetId: "under-the-sea-sight-florida-keys",
    },
    {
      rewardId: "under-the-sea-conch-compass",
      kind: "chest",
      title: "Conch Compass",
      unlock: { atPercent: 33 },
      assetId: "under-the-sea-item-conch-compass",
    },
    {
      rewardId: "under-the-sea-chemosynthesis-rune",
      kind: "chest",
      title: "Chemosynthesis Rune",
      unlock: { atPercent: 66 },
      assetId: "under-the-sea-item-chemosynthesis-rune",
    },
    {
      rewardId: "under-the-sea-abyssal-pressure-core",
      kind: "chest",
      title: "Abyssal Pressure Core",
      unlock: { completion: true },
      assetId: "under-the-sea-item-abyssal-pressure-core",
    },
    {
      rewardId: "under-the-sea-blue-atlas",
      kind: "badge",
      title: "Blue Atlas Complete",
      unlock: { completion: true },
      assetId: "under-the-sea-badge-blue-atlas",
    },
  ],
  shareMeta: {
    title: "Under the Sea",
    description: "Turn daily movement into a cheerful world-oceans expedition.",
    ogImageAssetId: "under-the-sea-hero",
  },
  sourceNotes: [
    "NOAA ETOPO Global Relief source map: https://www.ncei.noaa.gov/products/etopo-global-relief-model and public-domain NOAA image mirror: https://commons.wikimedia.org/wiki/File:NOAA_ETOPO_Global_Relief_map.jpg",
    "Natural Earth, GEBCO, and NOAA are preferred sources for future bathymetry/coastline refinements: https://www.gebco.net/data-products/gridded-bathymetry-data and https://www.naturalearthdata.com/",
    "Additional undersea feature coordinates use Marine Regions/GEBCO entries for Romanche Passage, South Sandwich Trench, Agulhas Ridge, Kerguelen Plateau, Ninetyeast Ridge, and Sunda Trench, with BGS context for Indian and Southern Ocean deeps.",
    "Waypoint coordinates are representative WGS84 points or centroids from NOAA, Marine Regions/GEBCO, UNESCO, NASA, USGS, BGS, and related public science references; they are not navigation-grade boundaries.",
    "Waypoint order is arranged as an ocean-basin expedition to keep visual route legs in water as much as possible on the equirectangular map.",
    "Route line is symbolic and uses full-world map rendering with segmented dateline-crossing strokes; it is not a navigation path.",
  ],
};

export const routeCatalog: QuestRoute[] = [
  moonTrekRoute,
  marsTraverseRoute,
  fourMoonsRoute,
  appalachianTrailRoute,
  pacificCrestTrailRoute,
  nationalParksGrandTourRoute,
  earthCityTourRoute,
  underTheSeaRoute,
];

export function getRouteByVersion(routeVersionId: string): QuestRoute | null {
  return (
    routeCatalog.find((route) => route.routeVersionId === routeVersionId) ??
    null
  );
}

function milesToMeters(miles: number): number {
  return Math.round(miles * 1_609.344);
}

function nationalParkStopMeters(index: number): number {
  if (nationalParkCheckpointData.length <= 1) return 0;
  return Math.round(
    (NATIONAL_PARKS_GAME_DISTANCE_METERS * index) /
      (nationalParkCheckpointData.length - 1),
  );
}

function chapterCompleteMeters(chapterId: string): number {
  let lastParkIndex = -1;
  nationalParkCheckpointData.forEach(([, , parkChapterId], index) => {
    if (parkChapterId === chapterId) lastParkIndex = index;
  });
  return nationalParkStopMeters(Math.max(0, lastParkIndex));
}

function chapterTitle(chapterId: string): string {
  return (
    nationalParksChapters.find((chapter) => chapter.chapterId === chapterId)
      ?.title ?? chapterId
  );
}

function earthCityStopMeters(index: number): number {
  if (earthCityWaypointData.length <= 1) return 0;
  return Math.round(
    (EARTH_CITY_TOUR_GAME_DISTANCE_METERS * index) /
      (earthCityWaypointData.length - 1),
  );
}

function earthCityChapterCompleteMeters(chapterId: string): number {
  let lastCityIndex = -1;
  earthCityWaypointData.forEach(([, , cityChapterId], index) => {
    if (cityChapterId === chapterId) lastCityIndex = index;
  });
  return earthCityStopMeters(Math.max(0, lastCityIndex));
}

function earthCityChapterTitle(chapterId: string): string {
  return (
    earthCityTourChapters.find((chapter) => chapter.chapterId === chapterId)
      ?.title ?? chapterId
  );
}

function wikipediaUrl(title: string): string {
  return `https://en.wikipedia.org/wiki/${encodeURIComponent(
    title.replaceAll(" ", "_"),
  )}`;
}

function nationalParkUrl(title: string): string {
  return wikipediaUrl(`${title} National Park`);
}

function underTheSeaStopMeters(index: number): number {
  if (underTheSeaWaypointData.length <= 1) return 0;
  return Math.round(
    (UNDER_THE_SEA_GAME_DISTANCE_METERS * index) /
      (underTheSeaWaypointData.length - 1),
  );
}
